import React from 'react';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Skeleton, Box } from '@mui/material';
import { CompanySubscription, Deal, ProductSubscriptionSuccessFeeTier } from '../../../shared/models';
import { EmptyGuid } from '../../../shared/constants';
import { CerebroDrawer, CerebroHeading, CerebroSecondaryButton, CerebroText } from '../../../shared/components';
import { NumberUtils } from '../../../shared/utils';
import { SuccessFeeTierActionButton } from '../SuccessFeeTierActionButton';
import { DeleteCompanySuccessFeeTierDialog } from './DeleteCompanySuccessFeeTierDialog';
import { CompanySuccessFeeTierFormDialog } from './CompanySuccessFeeTierFormDialog';
import { useCompanySubscriptionSuccessFeeTiersQuery } from '../../../shared/hooks';

interface CompanySubscriptionSuccessFeeTiersDrawerProps {
	onClose: () => void;
	subscription: CompanySubscription | null;
	deal: Deal;
}

export const CompanySubscriptionSuccessFeeTiersDrawer: React.FC<CompanySubscriptionSuccessFeeTiersDrawerProps> = ({
	subscription,
	deal,
	onClose,
}) => {
	const ref = React.useRef<HTMLDivElement>(null);
	const [tierForm, setTierForm] = React.useState<ProductSubscriptionSuccessFeeTier | null>(null);
	const [tierDelete, setTierDelete] = React.useState<ProductSubscriptionSuccessFeeTier | null>(null);
	const tiersQuery = useCompanySubscriptionSuccessFeeTiersQuery(deal.companyGuid, subscription?.guid, {
		enabled: subscription != null,
		retry: false,
	});
	const isFetching = tiersQuery.isLoading || tiersQuery.isFetching;

	const onCloseDialogs = () => {
		setTierForm(null);
		setTierDelete(null);
	};

	const createNewSuccessFeeTier = () => {
		setTierForm({
			guid: EmptyGuid,
			lowerFundingAmount: 0,
			basisPoints: 0,
		} as ProductSubscriptionSuccessFeeTier);
	};

	return (
		<CerebroDrawer ref={ref} open={subscription != null} onClose={onClose}>
			<CerebroHeading>Update Success Fee Tiers</CerebroHeading>

			<CerebroSecondaryButton onClick={() => createNewSuccessFeeTier()} sx={{ marginBottom: 2, marginTop: 3 }}>
				Add Tier
			</CerebroSecondaryButton>

			<TableContainer sx={{ marginBottom: 8 }}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>Lower Funding Amount</TableCell>
							<TableCell>Upper Funding Amount</TableCell>
							<TableCell>Bps</TableCell>
							<TableCell></TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{isFetching && (
							<TableRow>
								<TableCell colSpan={4}>
									<Skeleton height={60} />
								</TableCell>
							</TableRow>
						)}

						{tiersQuery.isSuccess && (
							<>
								{tiersQuery.data.map(tier => {
									return (
										<TableRow key={tier.guid}>
											<TableCell>{NumberUtils.FormatCurrency(tier.lowerFundingAmount)}</TableCell>
											<TableCell>{NumberUtils.FormatCurrency(tier.upperFundingAmount)}</TableCell>
											<TableCell>{tier.basisPoints}</TableCell>
											<TableCell>
												<SuccessFeeTierActionButton
													tier={tier}
													onEdit={setTierForm}
													onDelete={setTierDelete}
												/>
											</TableCell>
										</TableRow>
									);
								})}
								{tiersQuery.data.length == 0 && (
									<TableRow>
										<TableCell colSpan={4}>
											No success fee tiers are currently associated to the subscription.
										</TableCell>
									</TableRow>
								)}
							</>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			<Box>
				<CerebroText>Tier Validation Rules</CerebroText>
				<ul style={{lineHeight: 2}}>
					<li>
						<CerebroText>If success fee type is tiered, there must be at least one tier</CerebroText>
					</li>
					<li>
						<CerebroText>First tier must have a $0 lower funding amount</CerebroText>
					</li>
					<li>
						<CerebroText>Middle Tiers</CerebroText>
					</li>
					<ul>
						<li>
							<CerebroText>Must have a lower and upper funding amount</CerebroText>
						</li>
						<li>
							{' '}
							<CerebroText>Lower funding amount must be less than upper funding amount</CerebroText>
						</li>
						<li>
							<CerebroText>
								Lower funding amount must be equal to the previous tier upper funding amount
							</CerebroText>
						</li>
					</ul>
					<li>
						<CerebroText>The last tier must have no upper funding amount</CerebroText>
					</li>
				</ul>
			</Box>

			<CompanySuccessFeeTierFormDialog
				subscription={subscription}
				tier={tierForm}
				dealGuid={deal.guid}
				companyGuid={deal.companyGuid}
				close={onCloseDialogs}
			/>
			<DeleteCompanySuccessFeeTierDialog
				subscription={subscription}
				tier={tierDelete}
				dealGuid={deal.guid}
				companyGuid={deal.companyGuid}
				close={onCloseDialogs}
			/>
		</CerebroDrawer>
	);
};
